import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import EditTrackingService from '../../services/EditTrackingService';
import { formatUserName } from '../../utils/formatUtils';

export default function EditingLeaderboard({ folderPath, videoId, filterMode, dateRange = 'last30days', selectedLanguage = '' }) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [detailedStats, setDetailedStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState({});
    const [orderBy, setOrderBy] = useState('totalTime');
    const [order, setOrder] = useState('asc');
    const [expandedUser, setExpandedUser] = useState(null);

    useEffect(() => {
        fetchLeaderboard();
    }, [folderPath, videoId, filterMode, dateRange, selectedLanguage]);

    const fetchLeaderboard = async () => {
        setLoading(true);
        try {
            // Pass filter parameters to the service
            let data;
            if (videoId) {
                // If video ID is provided, get stats specific to that video
                data = await EditTrackingService.getVideoContributors(videoId);
            } else {
                data = await EditTrackingService.getGlobalLeaderboard('time', folderPath, dateRange, selectedLanguage);
            }
            setLeaderboard(data);
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDetails = async (userId, userLevel) => {
        const userKey = `${userId}-${userLevel}`;
        setDetailsLoading(prev => ({ ...prev, [userKey]: true }));
        
        try {
            // Fetch detailed stats for this user including video and language breakdown
            let detailedData;
            if (videoId) {
                // If viewing a specific video, get language breakdown for this video
                detailedData = await EditTrackingService.getUserVideoLanguageStats(userId, videoId);
            } else {
                // Otherwise get video breakdown
                detailedData = await EditTrackingService.getUserDetailedStats(userId, folderPath);
            }
            
            setDetailedStats(prev => ({
                ...prev,
                [userKey]: detailedData
            }));
        } catch (error) {
            console.error(`Error fetching details for user ${userId}:`, error);
        } finally {
            setDetailsLoading(prev => ({ ...prev, [userKey]: false }));
        }
    };

    const handleAccordionChange = (userId, userLevel) => (event, isExpanded) => {
        const userKey = `${userId}-${userLevel}`;
        setExpandedUser(isExpanded ? userKey : null);
        
        if (isExpanded && !detailedStats[userKey]) {
            fetchUserDetails(userId, userLevel);
        }
    };

    const formatTime = (ms) => {
        if (!ms) return '00:00:00';
        return EditTrackingService.formatTime(ms);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getLevelRank = (level) => {
        if (!level) return Number.MAX_SAFE_INTEGER;
        const match = String(level).match(/(\d+)/);
        if (match) {
            return Number.parseInt(match[1], 10);
        }
        return Number.MAX_SAFE_INTEGER;
    };

    const sortedLeaderboard = React.useMemo(() => {
        const stabilizedThis = leaderboard.map((el, index) => [el, index]);
        
        // Remove efficiency calculation from sorting
        stabilizedThis.sort((a, b) => {
            const aValue = a[0][orderBy] !== undefined ? a[0][orderBy] : 0;
            const bValue = b[0][orderBy] !== undefined ? b[0][orderBy] : 0;

            if (orderBy === 'userLevel') {
                const aRank = getLevelRank(a[0].userLevel);
                const bRank = getLevelRank(b[0].userLevel);
                if (aRank < bRank) return order === 'asc' ? -1 : 1;
                if (aRank > bRank) return order === 'asc' ? 1 : -1;

                const aText = String(a[0].userLevel || '');
                const bText = String(b[0].userLevel || '');
                const textCompare = aText.localeCompare(bText);
                if (textCompare !== 0) return order === 'asc' ? textCompare : -textCompare;
                return a[1] - b[1];
            }
            
            // Special case for totalTime: lower is better
            if (orderBy === 'totalTime') {
                if (aValue < bValue) {
                    return order === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return order === 'asc' ? 1 : -1;
                }
                return a[1] - b[1];
            } 
            
            // Default sorting for other fields (higher is better)
            if (bValue < aValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (bValue > aValue) {
                return order === 'asc' ? 1 : -1;
            }
            return a[1] - b[1]; // Maintain original order if values are equal
        });
        
        return stabilizedThis.map((el) => el[0]);
    }, [leaderboard, order, orderBy]);

    const kpis = React.useMemo(() => {
        const totalEditors = leaderboard.length;
        const totalTimeMs = leaderboard.reduce((sum, row) => sum + (row.totalTime || 0), 0);
        const totalHours = totalTimeMs / 3600000;
        const totalVideosTouched = leaderboard.reduce((sum, row) => sum + (row.videosCount || 0), 0);
        const totalLanguagesTouched = leaderboard.reduce((sum, row) => sum + (row.languagesCount || 0), 0);

        return {
            totalEditors,
            totalHours,
            totalVideosTouched,
            totalLanguagesTouched,
        };
    }, [leaderboard]);

    const dateRangeLabel = React.useMemo(() => {
        const labelMap = {
            last24hours: 'Last 24 Hours',
            last7days: 'Last 7 Days',
            last30days: 'Last 30 Days',
            last90days: 'Last 90 Days',
            currentMonth: 'Current Month',
            previousMonth: 'Previous Month',
            all: 'All Available Data',
        };
        return labelMap[dateRange] || 'Last 30 Days';
    }, [dateRange]);

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Team Leaderboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click any row to view editor details and breakdown.
                    </Typography>
                    {folderPath && folderPath !== '/' && (
                        <Typography variant="caption" color="text.secondary">
                            Filtered by folder: {folderPath}
                        </Typography>
                    )}
                    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" variant="outlined" label={`Range: ${dateRangeLabel}`} />
                        {selectedLanguage && (
                            <Chip size="small" variant="outlined" label={`Language: ${selectedLanguage}`} />
                        )}
                    </Box>
                </Box>
            </Box>
            
            {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Editors</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{kpis.totalEditors}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{kpis.totalHours.toFixed(2)}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Videos Touched</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{kpis.totalVideosTouched}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Languages Touched</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{kpis.totalLanguagesTouched}</Typography>
                        </Paper>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            {/* Table head remains mostly the same */}
                            <TableHead>
                                <TableRow>
                                    <TableCell width="60px">#</TableCell>
                                    <TableCell>Editor</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'userLevel'}
                                            direction={orderBy === 'userLevel' ? order : 'asc'}
                                            onClick={() => handleRequestSort('userLevel')}
                                        >
                                            Level
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'totalTime'}
                                            direction={orderBy === 'totalTime' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalTime')}
                                        >
                                            Total Time
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'totalEdits'}
                                            direction={orderBy === 'totalEdits' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalEdits')}
                                        >
                                            Total Edits
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'videosCount'}
                                            direction={orderBy === 'videosCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('videosCount')}
                                        >
                                            Videos
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'languagesCount'}
                                            direction={orderBy === 'languagesCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('languagesCount')}
                                        >
                                            Languages
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'sessionsCount'}
                                            direction={orderBy === 'sessionsCount' ? order : 'asc'}
                                            onClick={() => handleRequestSort('sessionsCount')}
                                        >
                                            Sessions
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Editor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedLeaderboard.map((editor, index) => {
                                    const userKey = `${editor.userId}-${editor.userLevel}`;
                                    return (
                                        <React.Fragment key={userKey}>
                                            <TableRow 
                                                hover
                                                onClick={() => {
                                                    if (expandedUser !== userKey) {
                                                        setExpandedUser(userKey);
                                                        if (!detailedStats[userKey]) {
                                                            fetchUserDetails(editor.userId, editor.userLevel);
                                                        }
                                                    } else {
                                                        setExpandedUser(null);
                                                    }
                                                }}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    '&:hover': { 
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{formatUserName(editor.email)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={editor.userLevel}
                                                        color={editor.userLevel === 'Level 1' ? 'primary' : 'secondary'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatTime(editor.totalTime)}</TableCell>
                                                <TableCell>{editor.totalEdits || 0}</TableCell>
                                                <TableCell>{editor.videosCount || 0}</TableCell>
                                                <TableCell>{editor.languagesCount || 0}</TableCell>
                                                <TableCell>{editor.sessionsCount}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {(editor.v2SessionsCount || 0) > 0 && (
                                                            <Chip label={`V2: ${editor.v2SessionsCount}`} size="small" color="success" variant="filled" sx={{ fontSize: '0.7rem', height: 20 }} />
                                                        )}
                                                        {(editor.v1SessionsCount || 0) > 0 && (
                                                            <Chip label={`V1: ${editor.v1SessionsCount}`} size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                                                        )}
                                                        {!(editor.v1SessionsCount || 0) && !(editor.v2SessionsCount || 0) && (
                                                            <Chip label="V1" size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            
                                            {/* Detailed stats row */}
                                            <TableRow>
                                                <TableCell colSpan={9} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Accordion 
                                                        expanded={expandedUser === userKey}
                                                        onChange={handleAccordionChange(editor.userId, editor.userLevel)}
                                                        sx={{ boxShadow: 'none', background: 'rgba(0,0,0,0.02)' }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            sx={{ display: 'none' }}
                                                        >
                                                            <Typography>Details</Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            {detailsLoading[userKey] ? (
                                                                <Box display="flex" justifyContent="center" p={2}>
                                                                    <CircularProgress size={24} />
                                                                </Box>
                                                            ) : detailedStats[userKey] ? (
                                                                <Box>
                                                                    {/* Video-specific language breakdown */}
                                                                    {videoId && detailedStats[userKey].languageBreakdown && (
                                                                        <Box>
                                                                            <Typography variant="subtitle2" gutterBottom>
                                                                                Language Breakdown
                                                                            </Typography>
                                                                            <List dense>
                                                                                {Object.entries(detailedStats[userKey].languageBreakdown).map(([lang, stats]) => (
                                                                                    <ListItem key={lang} divider>
                                                                                        <ListItemText
                                                                                            primary={lang}
                                                                                            secondary={`${formatTime(stats.time)} `}
                                                                                        />
                                                                                    </ListItem>
                                                                                ))}
                                                                            </List>
                                                                        </Box>
                                                                    )}
                                                                    
                                                                    {/* Global stats - show video breakdown */}
                                                                    {!videoId && detailedStats[userKey].videoBreakdown && (
                                                                        <Box>
                                                                            <Typography variant="subtitle2" gutterBottom>
                                                                                Top 5 Videos
                                                                            </Typography>
                                                                            <List dense>
                                                                                {Object.entries(detailedStats[userKey].videoBreakdown)
                                                                                    .sort((a, b) => b[1].time - a[1].time)
                                                                                    .slice(0, 5)
                                                                                    .map(([videoId, stats]) => (
                                                                                        <ListItem key={videoId} divider>
                                                                                            <ListItemText
                                                                                                primary={stats.videoName || videoId}
                                                                                                secondary={`${formatTime(stats.time)}  • ${(stats.languages || []).join(', ') || 'No language data'}`}
                                                                                            />
                                                                                        </ListItem>
                                                                                    ))
                                                                                }
                                                                            </List>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No detailed data available
                                                                </Typography>
                                                            )}
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                                
                                {leaderboard.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Paper
                        variant="outlined"
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            background: 'linear-gradient(90deg, rgba(25,118,210,0.04) 0%, rgba(0,0,0,0) 100%)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoOutlinedIcon fontSize="small" color="info" />
                            <Typography variant="body2" color="text.secondary">
                                Editing stats are retained for 90 days. Older records are automatically purged.
                            </Typography>
                        </Box>
                        <Tooltip title="Data older than 90 days is deleted by scheduled cleanup.">
                            <Chip size="small" label="Retention: 90 days" color="info" variant="outlined" />
                        </Tooltip>
                    </Paper>
                </>
            )}
        </Box>
    );
}